import { DungeonEntity } from '../entities/DungeonEntity';
import { PlayerEntity } from '../entities/PlayerEntity';
import { dungeonService } from './DungeonService';
import { mapService } from './MapService';
import { ActionDefinition, IPosition } from '../types/types';
import {
  AIBehaviorState,
  ActionDirectionType,
  DirectionEnum,
  DungeonEntityType,
  EffectShape,
  RangeAlgorithm,
} from '../types/enums';
import {
  getTargetPosition,
  isTileBlocking,
  getSwipeDirections,
  getConeCells,
  getDirectionFromPositions,
  canReachThroughCorner,
} from '../utils/map.utils';
import { euclideanDistance, chebyshevDistance } from '../utils/distance.utils';
import { resolveShapeCells } from '../utils/shape.utils';

const num = (p: Record<string, unknown> | undefined, key: string): number =>
  typeof p?.[key] === 'number' ? (p[key] as number) : 0;

class AIService {
  public hasLineOfSight(
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
  ): boolean {
    // Bresenham line algorithm
    const dx = Math.abs(toX - fromX);
    const dy = Math.abs(toY - fromY);
    const sx = fromX < toX ? 1 : -1;
    const sy = fromY < toY ? 1 : -1;
    let err = dx - dy;

    let currentX = fromX;
    let currentY = fromY;

    while (currentX !== toX || currentY !== toY) {
      // walls only — entities do not block vision
      if (isTileBlocking(currentX, currentY, false)) {
        return false;
      }

      const e2 = 2 * err;
      if (e2 > -dy) {
        err -= dy;
        currentX += sx;
      }
      if (e2 < dx) {
        err += dx;
        currentY += sy;
      }
    }

    return true;
  }

  public detectPlayer(
    monster: DungeonEntity,
    visionRange = 8,
  ): PlayerEntity | null {
    let best: PlayerEntity | null = null;
    let bestDistSq = Infinity;
    for (const player of dungeonService.getPlayers()) {
      const dx = player.positionX - monster.positionX;
      const dy = player.positionY - monster.positionY;
      const distSq = dx * dx + dy * dy;
      if (distSq > visionRange * visionRange) {
        continue;
      }
      if (
        !this.hasLineOfSight(
          monster.positionX,
          monster.positionY,
          player.positionX,
          player.positionY,
        )
      ) {
        continue;
      }
      if (distSq < bestDistSq) {
        bestDistSq = distSq;
        best = player;
      }
    }
    return best;
  }

  private getAffectedCells(
    monster: DungeonEntity,
    action: ActionDefinition,
    direction: DirectionEnum,
    targetX?: number,
    targetY?: number,
  ): IPosition[] {
    const cells: IPosition[] = [];

    if (!action.shapes) {
      return cells;
    }

    if (action.projectile) {
      const lr = action.projectile.range ?? 1;
      let cx = monster.positionX;
      let cy = monster.positionY;
      let impact: { x: number; y: number } | null = null;
      for (let i = 0; i < lr; i++) {
        const next = getTargetPosition(cx, cy, direction);
        if (isTileBlocking(next.x, next.y, false)) {
          break;
        }
        const blocker = dungeonService.getEntityAt(next.x, next.y, monster.id);
        if (blocker) {
          impact = { x: next.x, y: next.y };
          break;
        }
        cx = next.x;
        cy = next.y;
      }
      if (impact) {
        const footprint = resolveShapeCells(action.shapes, {
          targetX: impact.x,
          targetY: impact.y,
          casterX: impact.x,
          casterY: impact.y,
          direction,
          defaultRange: action.range,
        });
        cells.push(...(footprint.length ? footprint : [impact]));
      }
      return cells;
    }

    for (const shape of action.shapes) {
      switch (shape.type) {
        case EffectShape.SINGLE: {
          const cx = targetX ?? monster.positionX;
          const cy = targetY ?? monster.positionY;
          cells.push({ x: cx, y: cy });
          break;
        }

        case EffectShape.SWIPE: {
          const swipeDirs = getSwipeDirections(direction);
          for (const swipeDir of swipeDirs) {
            const swipePos = getTargetPosition(
              monster.positionX,
              monster.positionY,
              swipeDir,
            );
            cells.push({ x: swipePos.x, y: swipePos.y });
          }
          break;
        }

        case EffectShape.CONE: {
          const coneRange = shape.range ?? action.range ?? 3;
          const coneCells = getConeCells(
            monster.positionX,
            monster.positionY,
            direction,
            coneRange,
            false,
          );
          cells.push(...coneCells);
          break;
        }

        case EffectShape.CIRCLE: {
          const r = shape.range ?? 1;
          const cx = targetX ?? monster.positionX;
          const cy = targetY ?? monster.positionY;
          for (let dx = -r; dx <= r; dx++) {
            for (let dy = -r; dy <= r; dy++) {
              if (shape.excludeCenter && dx === 0 && dy === 0) {
                continue;
              }
              if (dx * dx + dy * dy <= r * r) {
                cells.push({ x: cx + dx, y: cy + dy });
              }
            }
          }
          break;
        }

        case EffectShape.CROSS: {
          const cx = targetX ?? monster.positionX;
          const cy = targetY ?? monster.positionY;
          cells.push({ x: cx, y: cy });
          cells.push({ x: cx + 1, y: cy });
          cells.push({ x: cx - 1, y: cy });
          cells.push({ x: cx, y: cy + 1 });
          cells.push({ x: cx, y: cy - 1 });
          break;
        }

        case EffectShape.LINE: {
          const lr = shape.range ?? action.range ?? 1;
          let cx = monster.positionX;
          let cy = monster.positionY;
          for (let i = 0; i < lr; i++) {
            const next = getTargetPosition(cx, cy, direction);
            cells.push({ x: next.x, y: next.y });
            cx = next.x;
            cy = next.y;
          }
          break;
        }

        case EffectShape.ROOM: {
          const room = mapService.getRoomAt(
            monster.positionX,
            monster.positionY,
          );
          if (room) {
            cells.push(...mapService.getRoomCells(room));
          }
          break;
        }

        default:
          break;
      }
    }

    return cells;
  }

  private wouldHitAllies(
    monster: DungeonEntity,
    action: ActionDefinition,
    direction: DirectionEnum,
    targetX?: number,
    targetY?: number,
  ): boolean {
    if (action.ignoreFriendlyFire === true) {
      return false;
    }

    if (action.friendlyFire !== true) {
      return false;
    }

    const affectedCells = this.getAffectedCells(
      monster,
      action,
      direction,
      targetX,
      targetY,
    );

    for (const cell of affectedCells) {
      const entity = dungeonService.getEntityAt(cell.x, cell.y, monster.id);
      if (entity && entity.type === DungeonEntityType.MONSTER) {
        return true;
      }
    }

    return false;
  }

  // Delivery distance beyond the aim anchor: projectile travel, charge dash,
  // or hitscan ray. Aim range itself (action.range) is added separately.
  private deliveryReach(action: ActionDefinition): number {
    if (action.projectile) {
      return action.projectile.range ?? 1;
    }
    const dash = action.effects?.find((e) => e.handler === 'dashLine');
    if (dash) {
      return num(dash.params, 'cells');
    }
    const hit = action.effects?.find((e) => e.handler === 'hitscan');
    if (hit) {
      return num(hit.params, 'range');
    }
    return 0;
  }

  private shapeExtent(action: ActionDefinition): number {
    return (action.shapes ?? []).reduce((m, s) => Math.max(m, s.range ?? 0), 0);
  }

  // Furthest cell the spell can affect from the caster. Origin-aware:
  //  - delivery (projectile/charge/hitscan) launches from the caster → travel
  //    distance (+ impact zone), aim range not added;
  //  - target-centered shapes (SINGLE/CIRCLE/CROSS/CONE — origin at the aim
  //    cell) → aim + extent;
  //  - caster-origin shapes (LINE/SWIPE) → extent only.
  private effectiveReach(action: ActionDefinition): number {
    const aim = action.range ?? 1;
    const delivery = this.deliveryReach(action);
    if (delivery > 0) {
      return delivery + this.shapeExtent(action);
    }
    let reach = aim;
    for (const s of action.shapes ?? []) {
      const ext = s.range ?? 0;
      switch (s.type) {
        case EffectShape.CIRCLE:
        case EffectShape.CROSS:
        case EffectShape.CONE:
          reach = Math.max(reach, aim + ext);
          break;
        case EffectShape.LINE:
          reach = Math.max(reach, ext);
          break;
        case EffectShape.SWIPE:
          reach = Math.max(reach, 1);
          break;
        default:
          break;
      }
    }
    return reach;
  }

  private opposite(dir: DirectionEnum): DirectionEnum {
    switch (dir) {
      case DirectionEnum.TOP:
        return DirectionEnum.BOTTOM;
      case DirectionEnum.BOTTOM:
        return DirectionEnum.TOP;
      case DirectionEnum.LEFT:
        return DirectionEnum.RIGHT;
      case DirectionEnum.RIGHT:
        return DirectionEnum.LEFT;
      case DirectionEnum['TOP-RIGHT']:
        return DirectionEnum['BOTTOM-LEFT'];
      case DirectionEnum['BOTTOM-LEFT']:
        return DirectionEnum['TOP-RIGHT'];
      case DirectionEnum['TOP-LEFT']:
        return DirectionEnum['BOTTOM-RIGHT'];
      case DirectionEnum['BOTTOM-RIGHT']:
        return DirectionEnum['TOP-LEFT'];
      default:
        return dir;
    }
  }

  // True if some anchor within aim range puts `target` inside the shape zone.
  private aoeCanCover(
    monster: DungeonEntity,
    action: ActionDefinition,
    target: DungeonEntity,
  ): boolean {
    const aim = action.range ?? 1;
    const ext = this.shapeExtent(action);
    if (
      chebyshevDistance(
        monster.positionX,
        monster.positionY,
        target.positionX,
        target.positionY,
      ) >
      aim + ext
    ) {
      return false;
    }
    const dir = getDirectionFromPositions(
      monster.positionX,
      monster.positionY,
      target.positionX,
      target.positionY,
    );
    // Anchor at the target, pulled back toward the caster while each step gets
    // closer. A perpendicular offset (or a degenerate/overshooting `dir`) can
    // floor the chebyshev distance above `aim`, so stop when a step no longer
    // reduces it — otherwise the loop never reaches `<= aim` and hangs the host.
    let ax = target.positionX;
    let ay = target.positionY;
    let dist = chebyshevDistance(monster.positionX, monster.positionY, ax, ay);
    while (dist > aim) {
      const back = getTargetPosition(ax, ay, this.opposite(dir));
      const next = chebyshevDistance(
        monster.positionX,
        monster.positionY,
        back.x,
        back.y,
      );
      if (next >= dist) {
        break;
      }
      ax = back.x;
      ay = back.y;
      dist = next;
    }
    const cells = resolveShapeCells(action.shapes, {
      targetX: ax,
      targetY: ay,
      casterX: monster.positionX,
      casterY: monster.positionY,
      direction: dir,
      defaultRange: action.range,
    });
    return cells.some(
      (c) => c.x === target.positionX && c.y === target.positionY,
    );
  }

  private canReachTarget(
    monster: DungeonEntity,
    action: ActionDefinition,
    target: DungeonEntity,
  ): DirectionEnum | null {
    if (!action.range || !action.directionType) {
      return null;
    }

    const algorithm = action.rangeAlgorithm || RangeAlgorithm.CHEBYSHEV;
    const distance =
      algorithm === RangeAlgorithm.EUCLIDEAN
        ? euclideanDistance(
            monster.positionX,
            monster.positionY,
            target.positionX,
            target.positionY,
          )
        : chebyshevDistance(
            monster.positionX,
            monster.positionY,
            target.positionX,
            target.positionY,
          );

    if (distance > this.effectiveReach(action)) {
      return null;
    }
    if (action.minRange !== undefined && distance < action.minRange) {
      return null;
    }

    const requiresLineOfSight = action.lineOfSight !== false;
    if (requiresLineOfSight) {
      if (
        !this.hasLineOfSight(
          monster.positionX,
          monster.positionY,
          target.positionX,
          target.positionY,
        )
      ) {
        return null;
      }
    }

    // Shared rule with the cast pipeline: a spell cannot reach a target
    // diagonally behind a wall corner unless the cut is allowed. Otherwise the
    // AI would waste a cast (it now aborts entirely, only burning cooldown).
    if (
      !canReachThroughCorner(
        monster.positionX,
        monster.positionY,
        target.positionX,
        target.positionY,
        {
          range: action.range,
          directionType: action.directionType,
          lineOfSight: action.lineOfSight,
          bypassDiagonalMiss: action.bypassDiagonalMiss,
        },
      )
    ) {
      return null;
    }

    switch (action.directionType) {
      case ActionDirectionType.ALL:
        return getDirectionFromPositions(
          monster.positionX,
          monster.positionY,
          target.positionX,
          target.positionY,
        );

      case ActionDirectionType.ALIGNED:
        return this.getAlignedDirection(
          monster.positionX,
          monster.positionY,
          target.positionX,
          target.positionY,
          action.range,
          true, // include diagonals
        );

      case ActionDirectionType.LIMITED_ALIGNED:
        return this.getAlignedDirection(
          monster.positionX,
          monster.positionY,
          target.positionX,
          target.positionY,
          action.range,
          false, // no diagonals
        );

      default:
        return null;
    }
  }

  private getAlignedDirection(
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
    maxRange: number,
    includeDiagonals: boolean,
  ): DirectionEnum | null {
    const dx = toX - fromX;
    const dy = toY - fromY;

    if (!includeDiagonals) {
      if (dx === 0 && dy !== 0) {
        const distance = Math.abs(dy);
        if (distance <= maxRange) {
          return dy > 0 ? DirectionEnum.TOP : DirectionEnum.BOTTOM;
        }
      } else if (dy === 0 && dx !== 0) {
        const distance = Math.abs(dx);
        if (distance <= maxRange) {
          return dx > 0 ? DirectionEnum.RIGHT : DirectionEnum.LEFT;
        }
      }
      return null;
    }

    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    if (dx === 0 && dy !== 0 && absDy <= maxRange) {
      return dy > 0 ? DirectionEnum.TOP : DirectionEnum.BOTTOM;
    }
    if (dy === 0 && dx !== 0 && absDx <= maxRange) {
      return dx > 0 ? DirectionEnum.RIGHT : DirectionEnum.LEFT;
    }

    if (absDx === absDy && absDx <= maxRange) {
      if (dx > 0 && dy > 0) {
        return DirectionEnum['TOP-RIGHT'];
      }
      if (dx > 0 && dy < 0) {
        return DirectionEnum['BOTTOM-RIGHT'];
      }
      if (dx < 0 && dy > 0) {
        return DirectionEnum['TOP-LEFT'];
      }
      if (dx < 0 && dy < 0) {
        return DirectionEnum['BOTTOM-LEFT'];
      }
    }

    return null;
  }

  /**
   * Whether `action` connects with `target` from the monster's CURRENT cell
   * right now (cooldown checked by the caller): self-buff (self, no shapes) →
   * always; self-AOE (self, shapes) → target inside the zone; targeted →
   * canReachTarget. Friendly-fire spells that would hit an ally → false.
   */
  public canCastNow(
    monster: DungeonEntity,
    action: ActionDefinition,
    target: DungeonEntity,
  ): boolean {
    if (action.parameter?.type === 'self') {
      const hasShapes = !!action.shapes && action.shapes.length > 0;
      if (!hasShapes) {
        return true;
      }
      if (this.wouldHitAllies(monster, action, monster.direction)) {
        return false;
      }
      const cells = this.getAffectedCells(monster, action, monster.direction);
      return cells.some(
        (c) => c.x === target.positionX && c.y === target.positionY,
      );
    }
    if (!action.directionType) {
      return false;
    }
    // Area spells (no projectile/dash/hitscan delivery, with a multi-cell zone)
    // can anchor offset so the player lands anywhere in the zone — not only at
    // the cell canReachTarget would pick. Single-target and delivery spells keep
    // the strict reach path.
    if (this.isAreaAction(action)) {
      if (!this.aoeCanCover(monster, action, target)) {
        return false;
      }
      const dir = getDirectionFromPositions(
        monster.positionX,
        monster.positionY,
        target.positionX,
        target.positionY,
      );
      return !this.wouldHitAllies(
        monster,
        action,
        dir,
        target.positionX,
        target.positionY,
      );
    }
    const direction = this.canReachTarget(monster, action, target);
    if (direction === null) {
      return false;
    }
    return !this.wouldHitAllies(
      monster,
      action,
      direction,
      target.positionX,
      target.positionY,
    );
  }

  private isAreaAction(action: ActionDefinition): boolean {
    if (action.projectile) {
      return false;
    }
    if (
      action.effects?.some(
        (e) => e.handler === 'dashLine' || e.handler === 'hitscan',
      )
    ) {
      return false;
    }
    // Only target-centered zones benefit from offset anchoring (cast so the
    // player lands in a non-central cell). CONE/LINE/SWIPE are directional and
    // keep the canReachTarget path (which resolves their aligned direction).
    const shapes = action.shapes ?? [];
    return shapes.some(
      (s) => s.type === EffectShape.CIRCLE || s.type === EffectShape.CROSS,
    );
  }

  /**
   * A* pathfinding toward a target.
   *
   * `ignoreEntities` lets planning succeed even when blocked by entities.
   * `stopAtAdjacent` treats the target as reached once chebyshev-adjacent,
   * useful when the target cell is itself blocked (typically the player) —
   * prevents monsters standing behind another monster from staying immobile
   * because direct pathfinding to the target fails.
   */
  public getMovementTowardsTarget(
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
    ignoreEntities = false,
    stopAtAdjacent = false,
  ): { x: number; y: number } | null {
    if (fromX === toX && fromY === toY) {
      return null;
    }
    if (stopAtAdjacent && chebyshevDistance(fromX, fromY, toX, toY) <= 1) {
      return null;
    }

    const maxIterations = 200;
    let iterations = 0;

    interface AStarNode {
      x: number;
      y: number;
      g: number; // cost from start
      h: number; // heuristic to target
      f: number; // total cost (g + h)
      parent: AStarNode | null;
    }

    const openList: AStarNode[] = [];
    const closedSet = new Set<string>();

    const nodeMap = new Map<string, AStarNode>();

    const posKey = (x: number, y: number) => `${x},${y}`;

    const startNode: AStarNode = {
      x: fromX,
      y: fromY,
      g: 0,
      h: chebyshevDistance(fromX, fromY, toX, toY),
      f: 0,
      parent: null,
    };
    startNode.f = startNode.g + startNode.h;

    openList.push(startNode);
    nodeMap.set(posKey(fromX, fromY), startNode);

    while (openList.length > 0 && iterations < maxIterations) {
      iterations++;

      openList.sort((a, b) => a.f - b.f);

      const current = openList.shift();
      if (!current) {
        break;
      }
      const currentKey = posKey(current.x, current.y);

      const reached =
        (current.x === toX && current.y === toY) ||
        (stopAtAdjacent &&
          current !== startNode &&
          chebyshevDistance(current.x, current.y, toX, toY) === 1);
      if (reached) {
        let node = current;
        while (node.parent && node.parent.parent !== null) {
          node = node.parent;
        }
        return { x: node.x, y: node.y };
      }

      closedSet.add(currentKey);

      const neighbors = mapService.getNeighbors(current.x, current.y);

      for (const neighbor of neighbors) {
        const neighborKey = posKey(neighbor.x, neighbor.y);

        if (closedSet.has(neighborKey)) {
          continue;
        }

        if (!mapService.isWalkable(neighbor.x, neighbor.y, ignoreEntities)) {
          continue;
        }

        if (
          !mapService.isDiagonalValid(
            current.x,
            current.y,
            neighbor.x,
            neighbor.y,
          )
        ) {
          continue;
        }

        // Diagonal = 1.414 (√2), cardinal = 1.
        const isDiagonal = neighbor.x !== current.x && neighbor.y !== current.y;
        const moveCost = isDiagonal ? 1.414 : 1;
        const tentativeG = current.g + moveCost;

        let neighborNode = nodeMap.get(neighborKey);

        if (!neighborNode) {
          neighborNode = {
            x: neighbor.x,
            y: neighbor.y,
            g: tentativeG,
            h: chebyshevDistance(neighbor.x, neighbor.y, toX, toY),
            f: 0,
            parent: current,
          };
          neighborNode.f = neighborNode.g + neighborNode.h;

          openList.push(neighborNode);
          nodeMap.set(neighborKey, neighborNode);
        } else if (tentativeG < neighborNode.g) {
          neighborNode.g = tentativeG;
          neighborNode.f = neighborNode.g + neighborNode.h;
          neighborNode.parent = current;
        }
      }
    }

    // No path found: if entities were not ignored, retry once ignoring them
    // to at least find a general direction.
    if (!ignoreEntities) {
      return this.getMovementTowardsTarget(
        fromX,
        fromY,
        toX,
        toY,
        true,
        stopAtAdjacent,
      );
    }

    return null;
  }

  public generatePatrolPath(
    startX: number,
    startY: number,
    radius = 3,
    numPoints = 4,
  ): IPosition[] {
    const path: IPosition[] = [];

    for (let i = 0; i < numPoints; i++) {
      const angle = (i / numPoints) * Math.PI * 2;
      const x = Math.round(startX + Math.cos(angle) * radius);
      const y = Math.round(startY + Math.sin(angle) * radius);

      if (!isTileBlocking(x, y, false)) {
        path.push({ x, y });
      }
    }

    return path;
  }

  public determineBehaviorState(
    playerVisible: boolean,
    hasLastKnownPosition: boolean,
    hasPatrolPath: boolean,
  ): AIBehaviorState {
    if (playerVisible) {
      return AIBehaviorState.COMBAT;
    }

    if (hasLastKnownPosition) {
      return AIBehaviorState.CHASE;
    }

    if (hasPatrolPath) {
      return AIBehaviorState.PATROL;
    }

    return AIBehaviorState.IDLE;
  }
}

export const aiService = new AIService();
